import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QuoteRequestStatus } from '@everplan/shared';
import { QUOTE_REQUEST_STATUS_LABELS } from '@everplan/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const STATUS_PILL_STYLES: Record<QuoteRequestStatus, string> = {
  pending: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400',
  received: 'bg-green-500/15 text-green-700 dark:text-green-400',
  no_response: 'bg-muted text-muted-foreground',
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const sent = new Date(dateStr);
  const diffMs = now.getTime() - sent.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 8) return `${diffWeeks} weeks ago`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} months ago`;
}

interface InquiryRow {
  id: string;
  vendorName: string;
  sentAt: string;
  status: QuoteRequestStatus;
  notes: string | null;
  item: {
    id: string;
    name: string;
    category: {
      id: string;
      name: string;
    };
  };
}

interface InquiriesTabProps {
  projectId: string;
  onSelectItem: (itemId: string) => void;
}

export default function InquiriesTab({ projectId, onSelectItem }: InquiriesTabProps) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'open' | 'all'>('open');

  const { data: inquiries, isLoading } = useQuery({
    queryKey: ['inquiries', projectId],
    queryFn: async () => {
      const res = await fetch(`/api/quote-requests?projectId=${projectId}`);
      if (!res.ok) throw new Error('Failed to fetch inquiries');
      return res.json() as Promise<InquiryRow[]>;
    },
    enabled: !!projectId,
  });

  const updateStatus = useMutation({
    mutationFn: async ({ requestId, status }: { requestId: string; status: QuoteRequestStatus }) => {
      const res = await fetch(`/api/quote-requests/${requestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed to update status');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries', projectId] });
    },
  });

  const filtered = inquiries?.filter((inq) =>
    filter === 'open' ? inq.status === 'pending' : true,
  ) ?? [];

  const openCount = inquiries?.filter((inq) => inq.status === 'pending').length ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <Button
          variant={filter === 'open' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('open')}
        >
          Open ({openCount})
        </Button>
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
        >
          All ({inquiries?.length ?? 0})
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading inquiries...</p>
      ) : filtered.length === 0 ? (
        <Card className="rounded-3xl bg-card shadow-ambient">
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground italic">
              {filter === 'open' ? 'No open inquiries.' : 'No inquiries recorded yet.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-3xl bg-card shadow-ambient">
          <CardContent className="pt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="label-caps pb-2 pr-4 text-xs font-medium text-muted-foreground">Vendor</th>
                  <th className="label-caps pb-2 pr-4 text-xs font-medium text-muted-foreground">Item</th>
                  <th className="label-caps pb-2 pr-4 text-xs font-medium text-muted-foreground">Category</th>
                  <th className="label-caps pb-2 pr-4 text-xs font-medium text-muted-foreground">Sent</th>
                  <th className="label-caps pb-2 pr-4 text-xs font-medium text-muted-foreground">Status</th>
                  <th className="label-caps pb-2 text-xs font-medium text-muted-foreground text-right">Age</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inq) => (
                  <tr
                    key={inq.id}
                    className={`border-b last:border-0 ${inq.status !== 'pending' ? 'opacity-60' : ''}`}
                  >
                    <td className="py-3 pr-4 font-medium">{inq.vendorName}</td>
                    <td className="py-3 pr-4">
                      <button
                        type="button"
                        onClick={() => onSelectItem(inq.item.id)}
                        className="text-primary hover:underline"
                      >
                        {inq.item.name}
                      </button>
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground">{inq.item.category.name}</td>
                    <td className="py-3 pr-4 tabular-nums">{formatDate(inq.sentAt)}</td>
                    <td className="py-3 pr-4">
                      <select
                        value={inq.status}
                        onChange={(e) =>
                          updateStatus.mutate({
                            requestId: inq.id,
                            status: e.target.value as QuoteRequestStatus,
                          })
                        }
                        className={`cursor-pointer appearance-none rounded-full border-0 px-2.5 py-0.5 text-xs font-medium outline-none ${STATUS_PILL_STYLES[inq.status]}`}
                      >
                        <option value="pending">{QUOTE_REQUEST_STATUS_LABELS.pending}</option>
                        <option value="received">{QUOTE_REQUEST_STATUS_LABELS.received}</option>
                        <option value="no_response">{QUOTE_REQUEST_STATUS_LABELS.no_response}</option>
                      </select>
                    </td>
                    <td className="py-3 text-right text-xs text-muted-foreground">
                      {timeAgo(inq.sentAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
